/**
 * 长报告导出 PDF —— Paged.js 方案（正式实现；旧的截图装箱方案 exportReportPdf.ts 保留在磁盘作回退）
 *
 * 思路：分页交给 Paged.js（CSS Paged Media polyfill，public/plugins/paged.js 全局挂载为
 * window.Paged），按 break-inside/before 规则真正「排版」出分页结果，再逐页 html2canvas
 * 截图交给 jsPDF——分页断点由浏览器排版引擎决定，不再手写高度装箱数学。
 *
 * 迭代过程中确认的关键实现要点（成因详见 PAGE_CSS 内各条注释）：
 * 1. 分页 CSS 必须通过 preview() 的 stylesheets 参数（{虚拟文件名: css文本} 对象形式）传给
 *    Paged.js 的解析器才会生效——内嵌 <style> 只影响视觉，break-inside/@page 全被忽略。
 * 2. 页面盒不用真实 A4 尺寸（内容区仅 ~718px，桌面版多列网格被硬塞进去挤压换行；报告的
 *    窄屏 @media 规则按视口宽度判定，在分页流程中不会触发）。改用「设计宽度 × A4 长宽比」
 *    的自定义页面盒（1280×1810px ≈ 1:√2），内容按接近设计稿的宽度正常排版、零重排，
 *    Paged.js 只决定断页位置，最后每页整体等比缩到 A4。
 * 3. 「蒙层」：.geo-sec.alt 半透明白背景被 html2canvas 在分片时画满整页——PDF 模式下直接
 *    去掉该背景（每页本就是白底），章节得以连续排布，不需要每章强制换页（否则章尾大片留白）。
 * 4. 图表横向溢出：canvas 快照图撑满容器 + 压制 echarts 内联像素宽。
 * 5. 大片空白：只对矮个原子块禁止截断（.geo-card 可能高于一页，整卡禁断会留大坑）；
 *    跳过无实质内容的空页面盒。
 *
 * 已知限制：
 * - vue-echarts 渲染在 <canvas> 上，cloneNode 会得到空白画布，因此克隆前把每个 <canvas>
 *   换成当前像素的 <img> 快照。
 * - 阴影/渐变跨页裁切等视觉细节未精修，先看分页与留白的整体效果。
 */

interface PagedPdfConfig {
  fileName: string
  /** 每页截图清晰度缩放 */
  scale: number
  onStatus?: (status: string) => void
  onProgress?: (current: number, total: number) => void
}

const DEFAULT_CONFIG: PagedPdfConfig = {
  fileName: '深度品牌洞察报告.pdf',
  scale: (window.devicePixelRatio || 1) > 1 ? 2 : 1.5,
}

// 页面盒：设计宽度 1280px × A4 比例(1:√2)。内容按桌面布局正常排版不挤压，
// 出 PDF 时每页整体等比缩到 A4（比例误差 <0.01%，无可见变形）。
// 取 1280 而非更窄：页边距 40px×2 后内容区 1200px，.geo-shell 内宽 1128px，
// 恰好容下报告中最宽的表格（.geo-intent-table min-width:1120px），不触发横向溢出
const PAGE_W_PX = 1280
const PAGE_H_PX = 1810

// 分页 CSS：经 preview() 的 stylesheets 参数交给 Paged.js 解析（内嵌 <style> 不会被分页引擎读取）。
// 布局覆盖规则以 .geo-pdf-flow 作用域限定，只影响克隆出来的导出副本，不影响正在浏览的页面
// （Paged.js 会把这份 CSS 插入 document 全局，导出完成后 polisher.destroy() 移除）。
const PAGE_CSS = `
  @page { size: ${PAGE_W_PX}px ${PAGE_H_PX}px; margin: 36px 40px; }
  .geo-pdf-flow .geo-nav { display: none !important; }
  .geo-pdf-flow.geo-report-v2 { height: auto !important; overflow: visible !important; }
  .geo-pdf-flow .geo-report-scroll {
    position: static !important;
    overflow: visible !important;
    height: auto !important;
    padding-top: 0 !important;
  }
  /* 屏幕端给 .geo-sec 开了 content-visibility 懒渲染、给板块开了 .fx-pre 进场过渡隐藏态
     （AnalysisReportPageV3.vue）。导出副本在视口外排版，必须强制完整渲染并还原隐藏态，
     否则章节全部被判为屏外 → 排版结果空白 / html2canvas 截到进场前的透明帧 */
  .geo-pdf-flow .geo-sec {
    content-visibility: visible !important;
    contain-intrinsic-size: none !important;
  }
  .geo-pdf-flow .fx-pre,
  .geo-pdf-flow .fx-in {
    opacity: 1 !important;
    transform: none !important;
    transition: none !important;
    animation: none !important;
  }
  /* Paged.js 拆分标题时会留下空的编号副本，其异常几何盒会让截图把蓝色渐变画满整页。
     仅在导出副本中隐藏空编号，保留正常小节编号。 */
  .geo-pdf-flow .geo-sh-n:empty { display: none !important; }
  /* 「蒙层」的根源：.geo-sec.alt 的半透明白背景在元素被分片时会被 html2canvas 画满整页、
     盖住同页上方的上一章内容。PDF 每页本就是白底，这层背景没有视觉意义，直接去掉——
     蒙层无从产生，章节也无需再强制每章换页（此前的一章一页导致章尾大片留白），
     可与上一章同页连续排布 */
  .geo-pdf-flow .geo-sec.alt {
    background: none !important;
    border-top: 0 !important;
    border-bottom: 0 !important;
  }
  /* 屏幕上的章间大留白（padding 58px）在连续分页里会放大页内空隙，收紧 */
  .geo-pdf-flow .geo-sec { padding: 26px 0 !important; }
  /* 矮个原子块禁止截断 */
  .geo-stat-row, .geo-ph, .geo-jc-stage, .geo-cover, .geo-ins {
    break-inside: avoid;
  }
  /* 卡片与多列网格默认禁止截断：Paged.js 分片 CSS Grid/卡片后，html2canvas 渲染残片
     会大面积丢内容、错位（实测第3/9/14页症状——双列卡片网格跨页处只剩卡头、正文消失）。
     但比一整页还高的块（图+大表格卡）不许截断就只能整体推到下页留下大坑（甚至放不进一页），
     所以导出前用 JS 实测高度，给高块加 .geo-pdf-splittable 放行拆分（表格在行边界断开是安全的）。
     注意：放行必须用 :not() 从 avoid 列表里排除，而不是用后置规则覆盖——Paged.js 的样式
     处理对 !important/层叠支持不完整，覆盖式写法实测不生效（高块仍被整体推页，页尾留大坑） */
  .geo-ch2-framework:not(.geo-pdf-splittable),
  .geo-card:not(.geo-pdf-splittable), .geo-g2:not(.geo-pdf-splittable),
  .geo-g3:not(.geo-pdf-splittable), .geo-g4:not(.geo-pdf-splittable),
  .geo-g6:not(.geo-pdf-splittable),
  .geo-risk-grid:not(.geo-pdf-splittable), .geo-source-layer-grid:not(.geo-pdf-splittable),
  .geo-source-gap-grid:not(.geo-pdf-splittable),
  .geo-tw:not(.geo-pdf-splittable), .geo-hit-matrix-wrap:not(.geo-pdf-splittable) {
    break-inside: avoid;
  }
  .geo-pdf-splittable { break-inside: auto !important; }
  /* 标题不落单：章节头/小节头后禁止紧跟分页 */
  .geo-ph, .geo-sh { break-after: avoid; }
  table { break-inside: auto; }
  table tr { break-inside: avoid; }
  thead { display: table-header-group; }
  /* 卡片被分页截断时，Paged.js 默认取消断口处的边框，补回来避免"敞口"观感 */
  .geo-pdf-flow .geo-card[data-split-to] { border-bottom: 1px solid #E6EEF8 !important; }
  .geo-pdf-flow .geo-card[data-split-from] { border-top: 1px solid #E6EEF8 !important; }
  /* 图表快照等图片一律不得超出容器（快照保留的是实时布局的像素宽，可能宽于打印内容区） */
  .geo-pdf-flow img { max-width: 100%; }
  .geo-pdf-canvas-img { break-inside: avoid; }
  /* echarts 会在容器及内层 div 写死初始化时的内联像素宽度（实时布局宽），
     用 !important 压过内联样式，否则包装层仍会横向溢出打印内容区 */
  .geo-pdf-flow [_echarts_instance_],
  .geo-pdf-flow [_echarts_instance_] > div {
    width: auto !important;
    max-width: 100% !important;
  }
  /* 根容器和滚动容器的多层渐变背景（radial-gradient + linear-gradient）
     在 Paged.js 分页后会充满每一页，产生大面积蓝色色块 */
  .geo-pdf-flow.geo-report-v2 {
    background: #ffffff !important;
  }
  .geo-pdf-flow .geo-report-scroll {
    background: #ffffff !important;
  }
`

// 「必须放行拆分」的实测高度阈值（实时布局像素）。打印内容区高 ~1738px，且打印宽度(1128px)
// 窄于实时布局、同一内容在打印里更高，取 900px：低于它的块整体保持不拆，高于它的块放行在行边界拆分
const SPLITTABLE_MIN_LIVE_H = 900

// 调试模式：URL 带 ?pdfDebug=1 时点「生成PDF」，只执行到 Paged.js 分页排版，
// 分页结果（真实 DOM）平铺展示在页面上供检查（红实线框=放行拆分的块，蓝虚线框=保持禁拆的 .geo-tw）；
// ?pdfDebug=canvas 则再多走一步：对每页执行 html2canvas 截图，平铺展示「截图画布」而非 DOM——
// 两种模式对照可区分问题出在分页排版阶段还是截图渲染阶段。两种模式都不组装 PDF。
// 右上角按钮关闭调试视图
const getPdfDebugMode = () => new URLSearchParams(window.location.search).get('pdfDebug')

const pdfLog = (...args: unknown[]) =>
  // eslint-disable-next-line no-console
  console.log('[pdf]', ...args)

/** 临时展开懒渲染章节，并返回恢复原内联值及优先级的函数。 */
function forceSectionsVisible(liveRoot: HTMLElement) {
  const sections = Array.from(liveRoot.querySelectorAll<HTMLElement>('.geo-sec'))
  const previous = sections.map(section => ({
    value: section.style.getPropertyValue('content-visibility'),
    priority: section.style.getPropertyPriority('content-visibility'),
  }))
  sections.forEach(section =>
    section.style.setProperty('content-visibility', 'visible', 'important'),
  )

  return () => {
    sections.forEach((section, index) => {
      const { value, priority } = previous[index]
      if (value) section.style.setProperty('content-visibility', value, priority)
      else section.style.removeProperty('content-visibility')
    })
  }
}

/** 按实时布局实测高度，给克隆副本里的高块打上 .geo-pdf-splittable（放行跨页拆分） */
function tagSplittableBlocks(liveRoot: HTMLElement, cloneRoot: HTMLElement, debug = false) {
  const SELECTOR =
    '.geo-ch2-framework, .geo-card, .geo-g2, .geo-g3, .geo-g4, .geo-g6, .geo-risk-grid, .geo-source-layer-grid, .geo-source-gap-grid, .geo-tw, .geo-hit-matrix-wrap'
  // 屏幕端 .geo-sec 开着 content-visibility 懒渲染：导出时刻屏外章节处于「跳过」状态，
  // 其内部块的 offsetHeight 测量不可靠 → 高块漏标 splittable，被当作原子块整体推到下一页，
  // 上一页留下大段空白（实测第9页症状）。测量期间用内联样式临时强制全量渲染，测完立即恢复
  const restoreVisibility = forceSectionsVisible(liveRoot)
  try {
    const liveEls = Array.from(liveRoot.querySelectorAll(SELECTOR)) as HTMLElement[]
    const cloneEls = Array.from(cloneRoot.querySelectorAll(SELECTOR)) as HTMLElement[]
    liveEls.forEach((el, i) => {
      const cloneEl = cloneEls[i]
      if (!cloneEl) return
      const h = el.offsetHeight
      if (h > SPLITTABLE_MIN_LIVE_H) cloneEl.classList.add('geo-pdf-splittable')
      if (debug) {
        const tag = cloneEl.classList.contains('geo-pdf-splittable') ? '放行拆分' : '保持不拆'
        pdfLog(`实测块 [${cloneEl.classList.value}] 实时高度=${h}px → ${tag}`)
      }
    })
  } finally {
    restoreVisibility()
  }
}

/** 让出一帧：分页排版与逐页截图都是长任务，不让出帧的话进度 UI 不会真正绘制 */
const nextFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))

/**
 * 资源就绪门：确保字体与图片全部加载解码完成再进入排版/截图。
 * 导出宁可慢一点也不能出内容缺失——字体未就绪会导致度量错位，图片未就绪会以 0 高参与
 * Paged.js 断页计算（分页错位）或被 html2canvas 画成空块/残块。
 */
async function waitFontsReady() {
  try {
    await document.fonts?.ready
  } catch {
    // 字体 API 不可用或加载失败：不阻塞导出
  }
}

/** 预热并解码一组图片地址（命中浏览器缓存），失败的图片不阻塞整体流程 */
function preloadImages(srcs: string[]) {
  return Promise.all(
    srcs.filter(Boolean).map(src => {
      const im = new Image()
      im.src = src
      return im.decode().catch(() => undefined)
    }),
  )
}

/** 等待容器内所有 <img> 解码完成（含 canvas 快照的 dataURL 大图），失败不阻塞 */
function waitImagesDecoded(root: HTMLElement) {
  return Promise.all(
    Array.from(root.querySelectorAll('img')).map(img =>
      img.decode ? img.decode().catch(() => undefined) : Promise.resolve(undefined),
    ),
  )
}

/** 把 <canvas> 替换为像素快照，单个空画布或不可读画布不影响后续图表。 */
async function snapshotCanvasesInto(liveRoot: HTMLElement, cloneRoot: HTMLElement) {
  const liveCanvases = Array.from(liveRoot.querySelectorAll('canvas'))
  const cloneCanvases = Array.from(cloneRoot.querySelectorAll('canvas'))
  for (let i = 0; i < liveCanvases.length; i += 1) {
    const liveCanvas = liveCanvases[i]
    const cloneCanvas = cloneCanvases[i]
    if (!cloneCanvas) continue
    // 0 宽或 0 高的 canvas 会导致 html2canvas 内部调用 createPattern 时抛出
    // "The image argument is a canvas element with a width or height of 0"；
    // 直接移除，避免 html2canvas 遇到它时崩溃
    if (!liveCanvas.width || !liveCanvas.height) {
      cloneCanvas.remove()
      // eslint-disable-next-line no-continue
      continue
    }
    let dataUrl = ''
    try {
      dataUrl = liveCanvas.toDataURL('image/png')
    } catch {
      cloneCanvas.remove()
      continue
    }
    const img = document.createElement('img')
    img.src = dataUrl
    img.className = 'geo-pdf-canvas-img'
    // 不写死实时布局的像素宽（可能宽于打印内容区导致横向溢出）：
    // 撑满父容器（图表外层多为固定高度盒），contain 保持图表原始比例不变形
    img.style.width = '100%'
    img.style.height = '100%'
    img.style.objectFit = 'contain'
    img.style.display = 'block'
    cloneCanvas.replaceWith(img)
    // eslint-disable-next-line no-await-in-loop
    await nextFrame()
  }
}

/** 移除容器内所有 0 宽或 0 高的 <canvas>，防止 html2canvas 的 createPattern 报错 */
function removeZeroSizeCanvases(el: HTMLElement) {
  el.querySelectorAll('canvas').forEach(c => {
    if (!c.width || !c.height) c.remove()
  })
}

/**
 * html2canvas 处理渐变背景时会临时创建 canvas。分页后的极小尺寸可能在赋值给
 * canvas.width/canvas.height 时被取整为 0，DOM 清理无法覆盖这种内部 canvas。
 * 导出期间忽略这类 pattern，避免浏览器原生 createPattern 直接中断整个 PDF。
 */
async function renderHtml2Canvas<T>(
  render: (onclone: (clonedDocument: Document) => void) => Promise<T>,
): Promise<T> {
  const prototype = CanvasRenderingContext2D.prototype
  const originalCreatePattern = prototype.createPattern

  prototype.createPattern = function (image, repetition) {
    const canvasImage = image as HTMLCanvasElement
    if (canvasImage instanceof HTMLCanvasElement && (!canvasImage.width || !canvasImage.height)) {
      return null
    }

    try {
      return Reflect.apply(originalCreatePattern, this, [image, repetition])
    } catch (error) {
      if (String(error).includes('width or height of 0')) {
        return null
      }
      throw error
    }
  }

  try {
    return await render((clonedDocument) => removeZeroSizeCanvases(clonedDocument.documentElement))
  } finally {
    prototype.createPattern = originalCreatePattern
  }
}

/**
 * 按报告章节分段克隆，避免一次 cloneNode(true) 长时间占用主线程。
 * Paged.js 仍然接收到完整 DOM，只是把克隆成本拆成多个可让出浏览器帧的批次。
 */
async function cloneReportTree(source: HTMLElement, onStatus?: (status: string) => void) {
  const clone = source.cloneNode(false) as HTMLElement
  const reportScroll = Array.from(source.children).find(child =>
    child.classList.contains('geo-report-scroll'),
  )

  const reportChildren = reportScroll ? Array.from(reportScroll.childNodes) : []
  const reportBlocks = reportChildren.filter(node => node.nodeType === Node.ELEMENT_NODE)
  let blockIndex = 0

  for (const child of Array.from(source.childNodes)) {
    if (child !== reportScroll) {
      clone.appendChild(child.cloneNode(true))
      // eslint-disable-next-line no-await-in-loop
      await nextFrame()
      continue
    }

    const scrollClone = reportScroll.cloneNode(false) as HTMLElement
    for (const reportChild of reportChildren) {
      scrollClone.appendChild(reportChild.cloneNode(true))
      if (reportChild.nodeType === Node.ELEMENT_NODE) {
        blockIndex += 1
        onStatus?.(`正在准备 PDF 副本（${blockIndex}/${reportBlocks.length}）…`)
      }
      // eslint-disable-next-line no-await-in-loop
      await nextFrame()
    }
    clone.appendChild(scrollClone)
  }

  return clone
}

export async function exportLongPagePdfPaged(
  root: HTMLElement,
  options: Partial<PagedPdfConfig> = {},
): Promise<{ save: () => void }> {
  const cfg = { ...DEFAULT_CONFIG, ...options }
  const Paged = (window as any).Paged
  if (!Paged) throw new Error('Paged.js 未加载（public/plugins/paged.js）')

  const [{ default: html2canvas }, { jsPDF: JsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ])

  // 报告的 CSS 变量（--geo-*）定义在 .geo-report-v2 上，root 传入的是内部滚动容器，
  // 需要向上找到这层再整体克隆，克隆副本才能带上这些变量的定义
  const styledRoot = (root.closest('.geo-report-v2') as HTMLElement) || root
  const debugMode = getPdfDebugMode()
  const debug = debugMode !== null
  cfg.onStatus?.('正在准备 PDF 副本…')
  const clone = await cloneReportTree(styledRoot, cfg.onStatus)
  // 临时强制所有章节可见，确保屏外章节的 echarts canvas 已渲染（否则尺寸为 0）
  // 用 try-finally 保证即使出错也能恢复原始样式
  const restoreVisibility = forceSectionsVisible(styledRoot)
  try {
    cfg.onStatus?.('正在准备图表快照…')
    await nextFrame() // 等待浏览器渲染
    await snapshotCanvasesInto(styledRoot, clone)
  } finally {
    restoreVisibility()
  }
  tagSplittableBlocks(styledRoot, clone, debug)
  // Vue 的离场过渡期间加载遮罩仍可能存在，只保留报告正文。
  clone
    .querySelectorAll('.geo-nav, .geo-loading-mask, .geo-share-expired')
    .forEach(el => el.remove())
  // 分页引擎会再次克隆 DOM；移除动画状态，避免截图捕获淡入中途的半透明帧。
  clone.querySelectorAll<HTMLElement>('.fx-pre, .fx-in').forEach(el => {
    el.classList.remove('fx-pre', 'fx-in', 'fx-soft', 'fx-flat')
    el.style.removeProperty('transition-delay')
  })
  // 副本使用分页 CSS 的完整渲染规则，避免原有内联懒渲染样式压过该规则。
  clone.querySelectorAll<HTMLElement>('.geo-sec').forEach(section => {
    section.style.removeProperty('content-visibility')
    section.style.removeProperty('contain-intrinsic-size')
  })
  clone.classList.add('geo-pdf-flow') // 布局覆盖规则的作用域标记，避免影响正在浏览的页面
  if (debug) {
    pdfLog('克隆完成：splittable 块数 =', clone.querySelectorAll('.geo-pdf-splittable').length)
  }

  await nextFrame() // 让调用方的进度 UI 先绘制出来，再进入长耗时的排版阶段
  cfg.onStatus?.('正在计算 PDF 分页…')

  // 排版前的资源就绪门：字体 + 克隆里引用的全部图片（Paged.js 排版时图片必须有真实尺寸）
  await waitFontsReady()
  await preloadImages(Array.from(clone.querySelectorAll('img')).map(img => img.src))

  // 离屏容器：必须挂在真实文档里才能触发布局（Paged.js 需要真实测量），但移出可视区域；
  // pdfDebug 调试模式下改为全屏平铺展示，供直接检查分页结果
  const container = document.createElement('div')
  container.style.cssText = debug
    ? 'position:fixed;inset:0;z-index:99999;overflow:auto;background:#64748b;padding:24px;'
    : 'position:fixed;left:-99999px;top:0;'
  document.body.appendChild(container)
  if (debug) {
    const style = document.createElement('style')
    style.id = 'geo-pdf-debug-style'
    // outline 不参与布局，不会影响分页断点，只做可视化标注
    style.textContent = `
      .pagedjs_pagebox { background: #fff; box-shadow: 0 8px 24px rgba(0,0,0,.35); margin: 0 auto 24px; }
      .geo-pdf-flow .geo-pdf-splittable { outline: 3px solid #dc2626 !important; outline-offset: -3px; }
      .geo-pdf-flow .geo-tw:not(.geo-pdf-splittable) { outline: 3px dashed #1677ff !important; outline-offset: -3px; }
    `
    document.head.appendChild(style)
  }

  let previewer: any
  try {
    previewer = new Paged.Previewer()
    // stylesheets 用 {虚拟文件名: css文本} 对象形式传入，Paged.js 解析后 @page/break 规则才生效
    await previewer.preview(clone.outerHTML, [{ 'geo-pdf-page.css': PAGE_CSS }], container)

    const allPageEls = Array.from(container.querySelectorAll('.pagedjs_pagebox')) as HTMLElement[]
    // 跳过无实质内容的空页（尾部 margin/padding 溢出可能挤出空页面盒）
    const pageEls = allPageEls.filter(p => {
      const content = p.querySelector('.pagedjs_page_content') as HTMLElement | null
      if (!content) return false
      return (
        Boolean((content.textContent || '').trim()) ||
        Boolean(content.querySelector('img, svg, canvas'))
      )
    })
    if (!pageEls.length) throw new Error('Paged.js 未生成分页结果')
    cfg.onStatus?.(`分页完成，共 ${pageEls.length} 页，正在生成图片…`)

    // pdfDebug：到此为止，平铺展示分页结果并输出落点日志，不进入 PDF 组装
    if (debug) {
      const pagesWithIntent = allPageEls
        .map((p, i) => (p.querySelector('.geo-intent-table') ? i + 1 : 0))
        .filter(Boolean)
      pdfLog(`分页完成：共 ${allPageEls.length} 页（有效 ${pageEls.length} 页）`)
      pdfLog(`意图表格（.geo-intent-table）分布在第 ${pagesWithIntent.join(', ')} 页`)
      if (debugMode === 'canvas') {
        // canvas 模式：逐页跑 html2canvas，把「截图结果」贴在分页 DOM 下方对照展示。
        // DOM 正常而截图异常 → 问题在 html2canvas 渲染；两者都异常 → 问题在 Paged 分页/CSS
        pdfLog('canvas 模式：开始逐页截图（页面下方为截图结果，与上方 DOM 对照）…')
        const canvasWrap = document.createElement('div')
        canvasWrap.style.cssText = 'border-top:6px solid #dc2626;margin-top:32px;padding-top:24px;'
        container.appendChild(canvasWrap)
        for (let i = 0; i < allPageEls.length; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          await waitImagesDecoded(allPageEls[i])
          // eslint-disable-next-line no-await-in-loop
          removeZeroSizeCanvases(allPageEls[i])
          // eslint-disable-next-line no-await-in-loop
          const cv = await renderHtml2Canvas((onclone) =>
            html2canvas(allPageEls[i], {
              scale: 1,
              useCORS: true,
              allowTaint: true,
              backgroundColor: '#ffffff',
              onclone,
            }),
          )
          cv.style.cssText =
            'display:block;margin:0 auto 24px;box-shadow:0 8px 24px rgba(0,0,0,.35);' +
            `width:${PAGE_W_PX}px;height:auto;`
          const label = document.createElement('div')
          label.textContent = `↓ 第 ${i + 1} 页截图`
          label.style.cssText = 'color:#fff;font-weight:700;text-align:center;margin:8px 0;'
          canvasWrap.appendChild(label)
          canvasWrap.appendChild(cv)
          pdfLog(`canvas 模式：第 ${i + 1} / ${allPageEls.length} 页截图完成`)
          // eslint-disable-next-line no-await-in-loop
          await nextFrame()
        }
      }
      pdfLog('调试视图已平铺展示（红框=放行拆分，蓝虚线=禁拆的 .geo-tw），右上角按钮关闭')
      const closeBtn = document.createElement('button')
      closeBtn.textContent = '关闭 PDF 调试视图'
      closeBtn.style.cssText =
        'position:fixed;top:16px;right:16px;z-index:100000;padding:10px 16px;border:none;' +
        'border-radius:8px;background:#dc2626;color:#fff;font-weight:700;cursor:pointer;'
      closeBtn.onclick = () => {
        try {
          previewer?.polisher?.destroy?.()
        } catch {
          // 清理失败可忽略
        }
        container.remove()
        closeBtn.remove()
        document.getElementById('geo-pdf-debug-style')?.remove()
      }
      document.body.appendChild(closeBtn)
      throw new Error('pdfDebug 调试模式：已展示分页结果，未生成 PDF')
    }

    cfg.onProgress?.(0, pageEls.length) // 排版完成，进入逐页渲染阶段

    const pdf = new JsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })
    const pageW = pdf.internal.pageSize.getWidth()
    const pageH = pdf.internal.pageSize.getHeight()

    for (let i = 0; i < pageEls.length; i += 1) {
      // 截图前确保本页所有图片（含图表快照 dataURL 大图）已解码，避免截到空块/半解码残块
      // eslint-disable-next-line no-await-in-loop
      await waitImagesDecoded(pageEls[i])
      // eslint-disable-next-line no-await-in-loop
      removeZeroSizeCanvases(pageEls[i])
      // eslint-disable-next-line no-await-in-loop
      const canvas = await renderHtml2Canvas((onclone) =>
        html2canvas(pageEls[i], {
          scale: cfg.scale,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          onclone,
        }),
      )
      if (i > 0) pdf.addPage()
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, pageW, pageH)
      // 图片已交给 jsPDF，立即释放逐页截图占用的像素内存。
      canvas.width = 0
      canvas.height = 0
      cfg.onProgress?.(i + 1, pageEls.length)
      // eslint-disable-next-line no-await-in-loop
      await nextFrame() // 每页之间让出一帧，进度 UI 才能真正刷新
    }

    return { save: () => pdf.save(cfg.fileName) }
  } finally {
    // Paged.js 会把解析后的样式插到 document 全局，必须清掉，否则导出后正常页面样式被污染。
    // pdfDebug 调试模式下保留现场供检查，由「关闭 PDF 调试视图」按钮负责清理
    if (!debug) {
      try {
        previewer?.polisher?.destroy?.()
      } catch {
        // 清理失败不影响导出结果
      }
      container.remove()
    }
  }
}
